import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import ShareBox from '../../components/ShareBox';
import { 
  Target, Shield, TrendingUp, ArrowRight, ArrowLeft,
  ChevronRight, Zap, AlertTriangle, CheckCircle, Clock, XCircle, BookOpen, Activity
} from 'lucide-react';

// FAQ Data for Schema
const faqData = [
  {
    question: "TP ne demek?",
    answer: "TP (Take Profit), fiyatın ulaşması beklenen hedef seviyesidir."
  },
  {
    question: "Stop Loss nedir?",
    answer: "Stop Loss, risk sınırıdır. İşlem senaryosunun geçersiz sayıldığı noktadır."
  },
  {
    question: "Stop Loss zarar garantisi midir?",
    answer: "Hayır. Amaç riski sınırlamaktır, zarar garantisi vermez."
  },
  {
    question: "TP seviyeleri kesin midir?",
    answer: "Hayır. TP seviyeleri olası hedef bölgeleridir, kesinlik içermez."
  },
  {
    question: "TP ve Stop neden önemlidir?",
    answer: "İşlem planının temelini oluşturur. Bu iki seviye birlikte pozisyonun risk/ödül yapısını tanımlar."
  }
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "TP ve Stop Loss Nedir? Kripto İşlemlerde Temel Mantık",
    "description": "TP (Take Profit) ve Stop Loss ne demek? Kripto işlemlerde hedef ve risk seviyeleri nasıl çalışır, basitçe öğrenin.",
    "author": {
      "@type": "Organization",
      "name": "Borsamex"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Borsamex",
      "logo": {
        "@type": "ImageObject",
        "url": "https://borsamex.com/logo512.png"
      }
    },
    "datePublished": "2025-12-24",
    "dateModified": "2025-12-24",
    "mainEntityOfPage": "https://borsamex.com/rehber/tp-ve-stop-loss-nedir"
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  }
];

const TpStopLossNedirPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="TP ve Stop Loss Nedir? Kripto İşlemlerde Temel Mantık | Borsamex"
        description="TP (Take Profit) ve Stop Loss ne demek? Kripto işlemlerde hedef ve risk seviyeleri nasıl çalışır, basitçe öğrenin. LONG / SHORT mantığını netleştirin."
        keywords="tp nedir, stop loss nedir, take profit nedir, zarar durdur nedir, kripto tp, kripto stop loss, risk yönetimi"
        canonical="https://borsamex.com/rehber/tp-ve-stop-loss-nedir"
        structuredData={structuredData}
      />

      {/* Breadcrumb */}
      <div className="border-b border-slate-800/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-white transition-colors">Ana Sayfa</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/rehber" className="hover:text-white transition-colors">Rehber</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">TP ve Stop Loss Nedir?</span>
          </div>
        </div>
      </div>

      {/* Article */}
      <article className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          
          {/* Header */}
          <motion.header initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <Badge className="bg-green-500/10 text-green-400 border-green-500/20">Temel</Badge>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Clock className="h-4 w-4" />
                <span>4 dk okuma</span>
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              TP ve Stop Loss Nedir?
            </h1>
            
            {/* Featured Snippet Target */}
            <div className="p-6 bg-slate-900/50 border border-slate-700/50 rounded-xl">
              <p className="text-lg text-slate-300 leading-relaxed">
                <strong className="text-green-400">TP (Take Profit)</strong>, fiyatın ulaşması beklenen olası hedef seviyesini ifade eder.
                <strong className="text-red-400"> Stop Loss</strong> ise işlem senaryosunun geçersiz sayıldığı risk sınırıdır.
              </p>
              <p className="text-slate-400 mt-4">
                Bu iki kavramın amacı kâr garantilemek değil, işlem planını yapılandırmaktır.
              </p>
            </div>
          </motion.header>

          {/* Content */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }}
            className="prose prose-invert prose-slate max-w-none"
          >
            
            {/* TP Section */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Target className="h-4 w-4 text-green-400" />
                </div>
                TP (Take Profit) Ne Demek?
              </h2>
              
              <p className="text-slate-300 mb-4">
                TP, açılan bir pozisyonda fiyatın ulaşması beklenen potansiyel hedef seviyesidir.
              </p>
              
              <p className="text-slate-300 mb-4">
                Genellikle sinyal sistemlerinde birden fazla TP bulunur:
              </p>
              
              <div className="space-y-2 mb-6">
                <div className="flex items-center gap-3 p-3 bg-green-900/20 rounded-lg border border-green-500/20">
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30">TP1</Badge>
                  <span className="text-slate-300">İlk hedef seviyesi</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-emerald-900/20 rounded-lg border border-emerald-500/20">
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">TP2</Badge>
                  <span className="text-slate-300">Ana hedef seviyesi</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-cyan-900/20 rounded-lg border border-cyan-500/20">
                  <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">TP3</Badge>
                  <span className="text-slate-300">Güçlü trend senaryosu</span>
                </div>
              </div>
              
              <p className="text-slate-400">
                Bu yapı, piyasanın farklı olasılıklarına göre esneklik sağlar.
              </p>
            </section>

            {/* Stop Loss Section */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-red-400" />
                </div>
                Stop Loss Ne Demek?
              </h2>
              
              <p className="text-slate-300 mb-4">
                Stop Loss, işlem senaryosunun artık geçerli sayılmadığı risk sınırıdır.
              </p>
              
              <p className="text-slate-300 mb-4">
                Amaç zarar etmek değil, <strong className="text-white">kontrolsüz riski engellemektir</strong>.
              </p>
              
              <p className="text-slate-300 mb-4">Fiyat bu seviyeye ulaştığında:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Senaryo geçersiz kabul edilir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Pozisyon mantığı bozulmuş sayılır</span>
                </li>
              </ul>
              
              <div className="flex items-center gap-3 p-3 bg-red-900/20 rounded-lg border border-red-500/20">
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">STOP</Badge>
                <span className="text-slate-300">Stop Loss, profesyonel işlem planlarının temelidir.</span>
              </div>
            </section>

            {/* Birlikte Kullanım */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-purple-400" />
                </div>
                TP ve Stop Loss Neden Birlikte Kullanılır?
              </h2>
              
              <p className="text-slate-300 mb-4">
                Bu iki seviye birlikte işlem planını tanımlar:
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <Card className="border-green-500/30 bg-green-950/20">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="h-5 w-5 text-green-400" />
                      <span className="font-bold text-white">TP</span>
                    </div>
                    <p className="text-slate-300 text-sm">Olası hedef</p>
                  </CardContent>
                </Card>
                
                <Card className="border-red-500/30 bg-red-950/20">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="h-5 w-5 text-red-400" />
                      <span className="font-bold text-white">Stop</span>
                    </div>
                    <p className="text-slate-300 text-sm">Olası risk</p>
                  </CardContent>
                </Card>
              </div>
              
              <p className="text-slate-400">
                Bu yapı olmadan işlem yapmak, yönü bilmeden hareket etmeye benzer.
              </p>
            </section>

            {/* Inline AI Sinyal Kutusu */}
            <section className="mb-12">
              <Card className="border-brand-green/30 bg-brand-green/5">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="h-6 w-6 text-brand-green" />
                    <span className="font-bold text-white">AI Sinyal Örneği</span>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold">ETH-USDT</span>
                      <Badge className="bg-green-500/20 text-green-400">LONG</Badge>
                    </div>
                    <p className="text-slate-400 text-sm mb-2">
                      TP1: $3,850 | TP2: $4,100 | TP3: $4,450 | Stop: $3,520
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Activity className="h-3 w-3" />
                      <span>Kademeli TP yapısı ile risk kontrollü senaryo</span>
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm mb-4">
                    Borsamex sinyallerinde TP ve Stop seviyeleri algoritmik analiz ile hesaplanır.
                  </p>
                  <Link to="/signals">
                    <Button variant="outline" className="border-brand-green text-brand-green hover:bg-brand-green/10 w-full">
                      Canlı Sinyalleri İncele <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </section>

            {/* Kripto TP Mantığı */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-amber-400" />
                </div>
                Kripto İşlemlerde TP Mantığı
              </h2>
              
              <p className="text-slate-300 mb-4">
                Kripto piyasası volatil olduğu için:
              </p>
              
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                  <span>Tek hedef yerine kademeli TP kullanımı yaygındır</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                  <span>Farklı fiyat senaryoları değerlendirilir</span>
                </li>
              </ul>
              
              <p className="text-slate-400">
                Bu yaklaşım, piyasa davranışına uyum sağlar.
              </p>
            </section>

            {/* Stop Loss Mantığı */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-red-400" />
                </div>
                Stop Loss Mantığı
              </h2>
              
              <p className="text-slate-300 mb-4">
                Stop seviyesi:
              </p>
              
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Teknik seviyelere göre belirlenir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Rastgele seçilmez</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Risk yapısına göre konumlandırılır</span>
                </li>
              </ul>
              
              <Card className="border-slate-700/50 bg-slate-900/30">
                <CardContent className="p-4">
                  <p className="text-slate-300 text-sm">
                    Stop Loss'un amacı "yanılmamak" değil, <strong className="text-white">yanıldığında kontrolü korumaktır</strong>.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Önemli Yanılgı */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                </div>
                Önemli Yanılgı
              </h2>
              
              <p className="text-slate-300 mb-4">
                TP ve Stop seviyeleri:
              </p>
              
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-3 text-slate-300">
                  <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Kesinlik içermez</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Garanti değildir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Olasılık temellidir</span>
                </li>
              </ul>
              
              <p className="text-slate-400">
                Piyasa koşulları değişebilir.
              </p>
            </section>

            {/* Borsamex */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-green/20 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-brand-green" />
                </div>
                Borsamex Sistem Yaklaşımı
              </h2>
              
              <p className="text-slate-300 mb-4">Borsamex sinyallerinde:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>TP ve Stop seviyeleri algoritmik analiz ile hesaplanır</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Risk yapısı önceliklendirilir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>AI yalnızca açıklama & bağlam sunar</span>
                </li>
              </ul>

              <div className="flex gap-4">
                <Link to="/ai-kripto-sinyalleri">
                  <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                    AI Sinyalleri Hakkında →
                  </Button>
                </Link>
              </div>
            </section>

            {/* İlgili Konular - Internal Links */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <BookOpen className="h-4 w-4 text-blue-400" />
                </div>
                İlgili Konular
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <Link to="/rehber/kripto-sinyali-nedir" className="group">
                  <Card className="border-purple-500/20 bg-slate-900/50 hover:border-purple-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Zap className="h-5 w-5 text-purple-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-purple-400 transition-colors">Kripto Sinyali Nedir?</p>
                          <p className="text-xs text-slate-500">Temel kavramları öğren</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/long-ve-short-ne-demek" className="group">
                  <Card className="border-blue-500/20 bg-slate-900/50 hover:border-blue-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <TrendingUp className="h-5 w-5 text-blue-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-blue-400 transition-colors">LONG ve SHORT Ne Demek?</p>
                          <p className="text-xs text-slate-500">Yön kavramlarını anla</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/yeni-baslayanlar-icin-risk-yonetimi" className="group">
                  <Card className="border-red-500/20 bg-slate-900/50 hover:border-red-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Shield className="h-5 w-5 text-red-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-red-400 transition-colors">Risk Yönetimi</p>
                          <p className="text-xs text-slate-500">Sermaye koruma stratejileri</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/btc-sinyalleri" className="group">
                  <Card className="border-amber-500/20 bg-slate-900/50 hover:border-amber-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <span className="text-amber-400 text-lg">₿</span>
                        <div>
                          <p className="font-medium text-white group-hover:text-amber-400 transition-colors">BTC Sinyalleri</p>
                          <p className="text-xs text-slate-500">Bitcoin özel sinyaller</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">Sık Sorulan Sorular</h2>
              
              <div className="space-y-4">
                {faqData.map((faq, idx) => (
                  <Card key={idx} className="border-slate-700/50 bg-slate-900/30">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-white mb-2 flex items-start gap-2">
                        <span className="text-brand-green">?</span>
                        {faq.question}
                      </h3>
                      <p className="text-slate-400 text-sm pl-5">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <ShareBox 
              title="TP ve Stop Loss Nedir? Kripto Islemlerde Temel Mantik"
              url="/rehber/tp-ve-stop-loss-nedir"
              hashtags="#kripto #stoploss #takeprofit #trading"
            />

          </motion.div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-8 border-t border-slate-800">
            <Link to="/rehber/kripto-sinyali-nedir" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Kripto Sinyali Nedir?
            </Link>
            <Link to="/rehber/long-ve-short-ne-demek" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              Sonraki: LONG ve SHORT Nedir?
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-green-900/20 to-brand-green/10 rounded-2xl border border-slate-700/50 text-center">
            <h3 className="text-xl font-bold text-white mb-3">
              Risk Yönetimini Öğrendin, Şimdi Uygula
            </h3>
            <p className="text-slate-400 mb-6">
              Ücretsiz kaydol ve TP/Stop seviyeli canlı sinyalleri takip et.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                Ücretsiz Başla <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

        </div>
      </article>
    </div>
  );
};

export default TpStopLossNedirPage;
